import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Box, Flex } from "theme-ui";
import { Colors, FontWeight, rawColorValues, Label2, PauseIcon, PlayIcon } from "../../index";
const secondsToMinutes = (seconds) => Math.floor(seconds / 60) + ":" + ("0" + Math.floor(seconds % 60)).slice(-2);
export const AudioPlayer = ({ tracks, lang = "en" }) => {
    var _a, _b;
    const [trackIndex, setTrackIndex] = useState(0);
    const [trackProgress, setTrackProgress] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    // Refs
    //const audioRef = useRef(new Audio(audio_file))
    const { audio_track, audio_artist, audio_file } = tracks[trackIndex];
    const audioRef = useRef(typeof Audio !== "undefined" ? new Audio(audio_file === null || audio_file === void 0 ? void 0 : audio_file.url) : undefined);
    const isReady = useRef(false);
    const intervalRef = useRef(0);
    // Destructure for conciseness
    const duration = (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.duration;
    const currentPercentage = duration
        ? `${(trackProgress / duration) * 100}%`
        : "0%";
    const trackStyling = `
    -webkit-gradient(linear, 0% 0%, 100% 0%, color-stop(${currentPercentage}, #fff), color-stop(${currentPercentage}, ${rawColorValues.grey[2]}))
  `;
    const startTimer = () => {
        // Clear any timers already running
        clearInterval(intervalRef.current);
        intervalRef.current = window.setInterval(() => {
            var _a;
            if ((_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.ended) {
                toNextTrack();
            }
            else {
                if (audioRef.current) {
                    setTrackProgress(audioRef.current.currentTime);
                }
            }
        }, 1000);
    };
    const onScrub = (value) => {
        var _a;
        // Clear any timers already running
        clearInterval(intervalRef.current);
        if (audioRef.current) {
            audioRef.current.currentTime = Number(value);
            setTrackProgress((_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.currentTime);
        }
    };
    const onScrubEnd = () => {
        // If not already playing, start
        if (!isPlaying) {
            setIsPlaying(true);
        }
        startTimer();
    };
    const toNextTrack = () => {
        if (trackIndex < tracks.length - 1) {
            setTrackIndex(trackIndex + 1);
        }
        else {
            setTrackIndex(0);
        }
    };
    useEffect(() => {
        var _a, _b;
        if (isPlaying) {
            (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.play();
            startTimer();
        }
        else {
            (_b = audioRef.current) === null || _b === void 0 ? void 0 : _b.pause();
        }
    }, [isPlaying]);
    // Handles cleanup and setup when changing tracks
    useEffect(() => {
        var _a;
        (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.pause();
        audioRef.current = new Audio(audio_file === null || audio_file === void 0 ? void 0 : audio_file.url);
        setTrackProgress(audioRef.current.currentTime);
        if (isReady.current) {
            audioRef.current.play();
            setIsPlaying(true);
            startTimer();
        }
        else {
            // Set the isReady ref as true for the next pass
            isReady.current = true;
        }
    }, [trackIndex]);
    useEffect(() => {
        // Pause and clean up on unmount
        return () => {
            var _a;
            (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.pause();
            clearInterval(intervalRef.current);
        };
    }, []);
    const thumb = {
        appearance: "none",
        width: "16px",
        height: "16px",
        bg: "currentcolor",
        borderRadius: 9999,
        borderColor: `${Colors.white}.0`,
        borderWidth: 3,
        borderStyle: "solid"
    };
    return (_jsxs(Flex, { "aria-label": "Audio player", role: "region", sx: {
            width: "100%",
            flex: 1,
            flexDirection: "column"
        }, children: [_jsxs(Flex, { sx: {
                    flex: 1,
                    width: "100%",
                    bg: `${Colors.blue}.2`,
                    color: `${Colors.white}.0`,
                    p: 24,
                    flexDirection: "column"
                }, children: [_jsxs(Flex, { sx: {
                            alignItems: "center",
                            mb: 24
                        }, children: [_jsx(PlayPauseButton, { lang: lang, isPlaying: isPlaying, onPlayPause: () => setIsPlaying(!isPlaying) }), _jsxs(Flex, { sx: { flexDirection: "column", ml: 24, flex: 1 }, children: [_jsx(Label2, { children: audio_artist }), _jsx(Label2, { sx: { fontWeight: FontWeight.textBold }, children: audio_track })] }), _jsxs(Flex, { sx: { flexDirection: "row" }, children: [_jsx(Label2, { children: secondsToMinutes(trackProgress) }), audioRef.current && ((_b = audioRef.current) === null || _b === void 0 ? void 0 : _b.duration) > 0 && (_jsx(Label2, { sx: { ml: 4, color: `${Colors.grey}.2` }, children: secondsToMinutes(audioRef.current.duration) }))] })] }), _jsx("input", { "aria-label": "Player timeline scrubber", type: "range", value: trackProgress, "aria-valuetext": secondsToMinutes(trackProgress), step: "1", min: "0", max: duration ? duration : `${duration}`, className: "progress", onChange: e => onScrub(e.target.value), onMouseUp: onScrubEnd, onKeyUp: onScrubEnd, style: { background: trackStyling }, sx: {
                            height: 3,
                            width: "100%",
                            bg: `${Colors.grey}.3`,
                            color: `${Colors.blue}.2`,
                            appearance: "none",
                            "&::-webkit-slider-thumb": thumb,
                            "&::-moz-range-thumb": thumb,
                            "&::-ms-thumb": thumb
                        } })] }), tracks.length > 1 && (_jsx(Flex, { sx: {
                    flexDirection: "column",
                    borderLeftStyle: "solid",
                    borderLeftColor: `${Colors.grey}.3`,
                    borderLeftWidth: 1,
                    borderBottomStyle: "solid",
                    borderBottomColor: `${Colors.grey}.3`,
                    borderBottomWidth: 1,
                    borderRightStyle: "solid",
                    borderRightColor: `${Colors.grey}.3`,
                    borderRightWidth: 1,
                    "& > div + div": {
                        borderTopStyle: "solid",
                        borderTopColor: `${Colors.grey}.3`,
                        borderTopWidth: "1px"
                    }
                }, children: tracks.map((track, index) => {
                    return (_jsx(TrackItem, { lang: lang, current: trackIndex === index, track: track, onClick: () => setTrackIndex(index) }, `${track.audio_track}-${index}`));
                }) }))] }));
};
const TrackItem = props => {
    const { audio_track, audio_artist, audio_track_length } = props.track;
    return (_jsx(Flex, { sx: { alignItems: "center", px: 24, py: 24 }, children: _jsxs("button", { onClick: props.onClick, "aria-label": props.lang === "is"
                ? ` Spila ${audio_track} eftir ${audio_artist}"`
                : `Play ${audio_track} by ${audio_artist}`, sx: {
                width: "100%",
                variant: `buttons.accessible`,
                display: "flex",
                textAlign: "left",
                justifyContent: "center",
                alignItems: "center"
            }, children: [_jsx(Flex, { "aria-hidden": true, sx: {
                        justifyContent: "center",
                        alignItems: "center",
                        mr: 16
                    }, children: _jsx(Box, { sx: {
                            width: "8px",
                            height: "8px",
                            borderRadius: 9999,
                            bg: props.current ? `${Colors.blue}.2` : "transparent",
                            borderStyle: props.current ? `none` : "solid",
                            borderWidth: 1,
                            borderColor: `${Colors.grey}.1`
                        } }) }), _jsxs(Flex, { sx: { flex: 1, flexDirection: "column" }, children: [_jsx(Label2, { children: audio_artist }), _jsx(Label2, { sx: { fontWeight: FontWeight.textBold }, children: audio_track })] }), _jsx(Label2, { children: audio_track_length })] }) }));
};
const PlayPauseButton = ({ isPlaying = false, onPlayPause, lang }) => {
    return (_jsx("button", { onClick: onPlayPause, "aria-label": isPlaying
            ? lang === "is"
                ? "Stoppa "
                : "Pause"
            : lang === "is"
                ? "Spila"
                : "Play", sx: {
            width: 56,
            height: 56,
            bg: "transparent",
            borderRadius: 9999,
            m: 0,
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: `${Colors.white}.0`,
            borderColor: `${Colors.white}.0`,
            borderWidth: 3,
            borderStyle: "solid"
        }, children: isPlaying ? _jsx(PauseIcon, {}) : _jsx(PlayIcon, {}) }));
};
export default AudioPlayer;
//# sourceMappingURL=AudioPlayer.js.map